// What the sidebar DRAWS: the rows, their order, their labels, icons and
// attention signals. The desktop rail, the off-canvas panel below 1024px and
// active-item detection all read this file, so the three can never drift apart.
// per docs/ui-shape.md §Sidebar application shell (owner decision, 2026-08-26)
//
// WHO gets a row is not here. It moved to `app/access-model.ts`, the single
// source both this file and the route guard read, because "who is offered an
// entry" and "who may open the address" are two questions and one predicate
// could only answer one of them.
// per docs/shape-ops-access-model.md §The model (owner ruling, 2026-09-05)
import type { IconName } from '@egov-moldova/mud';
import type { Role } from '@simsm/shared-types';
import { isOfferedTo } from '../../app/access-model';
import type { NavContext } from '../../app/access-model';

// The vocabulary access is written in lives with the rules. Re-exported here,
// not restated: the sidebar reads its context and its highlight through the
// names it always used, and there is still exactly one definition of each.
export { activeSegment, isReporter, isSystemAdmin, navContextFor } from '../../app/access-model';
export type { NavContext };

// Sections group the rail the way caiet §4.2 groups the menu, in its order: a
// §4.2 menu with sub-items is a heading over its rows (Raportare stocuri,
// Stocuri, Administrare), and one with none is a row in a section with no
// heading (Acasă, Rapoarte, Tablouri de bord), set apart by the rule the
// Sidebar draws between sections. A section renders only when the role has at
// least one item in it, so section visibility cannot drift from item visibility.
// per docs/shape-ops-menu-alignment.md §Sections follow the §4.2 hierarchy
export type NavSectionId = 'main' | 'reporting' | 'reports' | 'stocks' | 'dashboards' | 'admin';

export type NavSection = {
  id: NavSectionId;
  // null for a §4.2 menu that is a single row and needs no heading.
  labelKey: string | null;
};

export const NAV_SECTIONS: ReadonlyArray<NavSection> = [
  { id: 'main', labelKey: null },
  { id: 'reporting', labelKey: 'nav.section.reporting' },
  { id: 'reports', labelKey: null },
  { id: 'stocks', labelKey: 'nav.section.stocks' },
  { id: 'dashboards', labelKey: null },
  { id: 'admin', labelKey: 'nav.section.admin' },
];

// Caiet §4.2: items that need attention are demarcated. The item names the
// signal; the sidebar resolves the count from the hook that owns it.
export type AttentionKey = 'drafts' | 'corrections' | 'alerts';

// NC-MENU-18, UC-ALRT-01: the indicator on «Alerte și notificări» leads to the predefined alert
// dashboard «Centrul de alerte» (a /dashboards/:slug page, under the active role's prefix), while
// the row itself reaches alert management.
export const ALERT_DASHBOARD_SEGMENT = 'dashboards/centrul-de-alerte';

export type NavItem = {
  // The key into ACCESS_RULES as well as the route segment. One target, one
  // rule; a row with no rule is offered to nobody.
  to: string;
  labelKey: string;
  // MUD icon name (see @egov-moldova/mud assets/icons.manifest.json).
  icon: IconName;
  section: NavSectionId;
  attention?: AttentionKey;
};

export const NAV_ITEMS: ReadonlyArray<NavItem> = [
  {
    to: 'home',
    labelKey: 'nav.home',
    icon: 'home-line',
    section: 'main',
  },

  {
    to: 'reporting',
    labelKey: 'nav.reporting',
    icon: 'page-add',
    section: 'reporting',
  },
  // Raporturi nefinalizate. An entry of its own, with the clock and the drafts
  // count, in both the wireframe menu (wireframes/react/src/lib/menu.ts:16) and
  // caiet §4.2. It used to be folded into the entry above as a badge, because
  // there was no screen to lead to; owner decision D4 of
  // docs/decizii-paritate-wireframe-2026-09-05.md built the screen, so the badge
  // moves onto the row it actually counts. A count on "Raport nou" said how many
  // drafts existed while offering no way to reach them.
  {
    to: 'drafts',
    labelKey: 'nav.drafts',
    icon: 'clock',
    section: 'reporting',
    attention: 'drafts',
  },
  // Solicitări corectare dată expirare. The wireframe hangs a counter of the
  // reporter's PENDING requests on this row (wireframes/react/src/lib/menu.ts:17,
  // Shell.tsx:53). It had none until owner decision D8 gave the reporter a count
  // of their own — `GET /corrections/mine/counts`, the operator's tenant, not the
  // administrator's queue, whose `/corrections/pending` refuses a reporter
  // outright.
  {
    to: 'corrections',
    labelKey: 'nav.corrections',
    icon: 'calendar-edit',
    section: 'reporting',
    attention: 'corrections',
  },

  // Rapoarte, then Stocuri with its two rows, then Tablouri de bord: caiet §4.2's
  // order. Anything else these roles can see lives inside one of these screens,
  // not beside it.
  {
    to: 'reports',
    labelKey: 'nav.reports',
    icon: 'page-text',
    section: 'reports',
  },
  {
    to: 'stocks',
    labelKey: 'nav.stocks_view',
    icon: 'chart',
    section: 'stocks',
  },
  {
    to: 'stocks/verify',
    labelKey: 'nav.cross_imsp',
    icon: 'search',
    section: 'stocks',
  },
  // No expiring-lots row. Caiet §4.2 has none, and the owner's reconciliation of
  // 2026-10-12 (T033) rules it over parity decision D7, which kept one for the
  // Observers. Expiry is a column of the stock view, and every role reaches the
  // dedicated screen by the link that view draws: the address is open to them,
  // the row is offered to no one, and access-model states both.
  // The dashboard read side is the owner-granted exception to "exactly the
  // wireframe menu" (caiet UC26, decision Î2): the wireframe gives the System
  // Administrator no dashboard entry at all.
  //
  // ONE entry, not two. The owner ruled on 2026-08-31 that the separate
  // "Grafana" entry is no longer needed — "el nu mai e encesar!!!! poate lesi
  // sub tablouri de baord wigetul grafana" — and on 2026-09-01 (#5) how the one
  // that survives divides from the administration screen: this entry READS —
  // `pages/dashboards/DashboardsPage.tsx` embeds the dashboards this role may
  // open in Grafana's kiosk presentation, with no editing chrome — while
  // "Gestiune tablouri" below EDITS, beside the role sharing that cannot leave
  // SIMSM.
  {
    to: 'dashboards',
    labelKey: 'nav.dashboards',
    icon: 'dot-grid',
    section: 'dashboards',
  },
  // No alert inbox here. Owner decision D7
  // (docs/decizii-paritate-wireframe-2026-09-05.md): the wireframe's observer menu
  // does not carry one and no caiet clause grants one — UC-ALRT-01 gives the alert
  // dashboard to the System Administrator, who reads fired alerts on the Declansari
  // tab of Alerte si notificari, and UC-ALRT-07 delivers to everyone else by e-mail
  // and webhook.

  // §Administrare — the wireframe's nine entries, in its order. Three tools that
  // used to sit here are not gone, they are where the wireframe puts them:
  // MConnect credentials inside Configurari (§7 of AdminConfigurari), MConnect
  // events inside Entitati raportoare (RSUD resync + the entity's history), and
  // Roluri observator inside Gestiune tablouri, which is where a role is bound
  // to the dashboards it may see (caiet UC26-02, decision I3). Their addresses
  // outlive their rows and are aliased onto them in access-model.
  {
    to: 'medicines',
    labelKey: 'nav.medicines',
    icon: 'health',
    section: 'admin',
  },
  {
    to: 'geography',
    labelKey: 'nav.geography',
    icon: 'map-pin',
    section: 'admin',
  },
  {
    to: 'registrars',
    labelKey: 'nav.registrars',
    icon: 'building',
    section: 'admin',
  },
  {
    to: 'sites',
    labelKey: 'nav.sites',
    icon: 'store',
    section: 'admin',
  },
  {
    to: 'credentials',
    labelKey: 'nav.credentials',
    icon: 'shield-keyhole',
    section: 'admin',
  },
  {
    to: 'approvals',
    labelKey: 'nav.approvals',
    // The wireframe draws a checklist — short lines with markers. AGE's `checklist`
    // is a document behind a magnifier; `bullet-list` is the glyph the design shows.
    icon: 'bullet-list',
    section: 'admin',
    attention: 'corrections',
  },
  {
    to: 'aggregates',
    labelKey: 'nav.aggregates',
    // Stacked sheets in the wireframe. AGE has no `layers`; `copy` is two overlapping
    // sheets, where `templates` was a document with a plus.
    icon: 'copy',
    section: 'admin',
  },
  {
    to: 'alerts',
    labelKey: 'nav.alerts',
    // A bell in the wireframe. AGE's `alarm` is a siren with rays; `notification` is
    // the bell.
    icon: 'notification',
    section: 'admin',
    attention: 'alerts',
  },
  // Configurări — the wireframe's ninth admin entry. It waited for a real page:
  // the screen and GET /admin/system-config both landed, so the row now leads
  // somewhere. Route segment is `settings`, English per R3, label Romanian.
  {
    to: 'settings',
    labelKey: 'nav.settings',
    icon: 'settings',
    section: 'admin',
  },
  // UC26-01/02 management surface: dashboards are created and shared with the
  // Observers from here, without a restart, and since the owner's ruling of
  // 2026-09-01 (#5) the panels are drawn here too — Grafana with its editing
  // chrome is embedded on the screen, beside the sharing, so the administrator
  // builds the dashboard and decides who gets it in one place.
  {
    to: 'dashboards-admin',
    labelKey: 'nav.dashboards_admin',
    icon: 'carousel',
    section: 'admin',
  },
];

// Before the session resolves only Acasa is offered: guessing a role's menu and
// then swapping it under the user is worse than one item that is always true.
export function navItemsFor(role: Role | undefined, context?: NavContext): ReadonlyArray<NavItem> {
  if (role === undefined) return NAV_ITEMS.slice(0, 1);
  return NAV_ITEMS.filter((item) => isOfferedTo(item.to, role, context));
}

export type NavGroup = { section: NavSection; items: ReadonlyArray<NavItem> };

export function navSectionsFor(
  role: Role | undefined,
  context?: NavContext,
): ReadonlyArray<NavGroup> {
  const visible = navItemsFor(role, context);
  return NAV_SECTIONS.map((section) => ({
    section,
    items: visible.filter((item) => item.section === section.id),
  })).filter((group) => group.items.length > 0);
}
